import { Link } from "react-router-dom";

// Body of the "Construction Compliance Checklist in India" article.
const P = ({ children }) => (
  <p className="mt-4 text-[15.5px] leading-[1.75] text-slate-700">{children}</p>
);
const H2 = ({ children, id }) => (
  <h2 id={id} className="mt-14 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
    {children}
  </h2>
);
const H3 = ({ children, id }) => (
  <h3 id={id} className="mt-8 font-display text-xl font-semibold tracking-tight text-ink">
    {children}
  </h3>
);
const UL = ({ children }) => (
  <ul className="mt-4 space-y-2 text-[15.5px] leading-relaxed text-slate-700">{children}</ul>
);
const LI = ({ children }) => (
  <li className="flex gap-3">
    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-greenaccent" />
    <span>{children}</span>
  </li>
);

export default function ConstructionComplianceChecklistIndia() {
  return (
    <article className="font-sans">
      <P>
        Construction in India is one of the most compliance-heavy industries. Between contractor licences, labour regulations, safety approvals, equipment certifications and insurance policies, a mid-sized construction business can easily juggle dozens of documents — each with its own expiry or renewal date. A single missed renewal can hold up a site, invite penalties or delay payments.
      </P>
      <P>
        This guide is a practical construction compliance checklist for Indian contractors and construction companies. It covers the kinds of documents that typically need tracking, explains why manual tracking becomes difficult at scale, and outlines a simple approach to keep every renewal date under control.
      </P>
      <p className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 text-xs italic leading-relaxed text-slate-500">
        This article is general information for planning purposes only. It is not legal advice. Compliance requirements vary by state, project type, contract value and authority — always verify current rules with the relevant department, licensing body, insurer or your legal advisor.
      </p>

      <H2 id="why-compliance-tracking">Why compliance tracking is important for construction businesses</H2>
      <P>
        Construction sites operate under multiple layers of regulation — central, state and municipal — and often under contract obligations from clients as well. Renewals that lapse can create real, measurable problems:
      </P>
      <UL>
        <LI>Site work can be paused or shut down if a mandatory licence expires.</LI>
        <LI>Insurance gaps can leave the business exposed on materials, equipment or workers.</LI>
        <LI>Contractor bills and running invoices can be withheld until compliance documents are current.</LI>
        <LI>Failed audits and inspections can affect eligibility for future tenders.</LI>
      </UL>
      <P>
        In short, compliance is not a one-time filing exercise — it is an ongoing tracking problem. And the more sites, vehicles, workers and equipment a company adds, the harder that tracking becomes.
      </P>

      <H2 id="documents-checklist">Documents, licences and certificates that may have renewal dates</H2>
      <P>
        The exact list depends on your state, project and contract. The categories below are the ones most Indian construction businesses encounter and are provided as examples, not as legal requirements.
      </P>

      <H3 id="contractor-licence">Contractor licence and registration renewals</H3>
      <UL>
        <LI>Contractor licence issued by the relevant public works or municipal authority</LI>
        <LI>Registration with state PWD, CPWD, MES or similar departments</LI>
        <LI>GST registration and periodic returns</LI>
        <LI>Shops &amp; Establishments registration (state-specific)</LI>
        <LI>Trade licence from the local municipal corporation</LI>
        <LI>Class-based contractor classification renewals</LI>
        <LI>Environmental clearances or NOCs relevant to the project</LI>
      </UL>

      <H3 id="labour-safety">Labour and safety-related compliance documents</H3>
      <UL>
        <LI>BOCW (Building &amp; Other Construction Workers) registration and cess-related filings</LI>
        <LI>Contract Labour (Regulation &amp; Abolition) Act registration/licence, where applicable</LI>
        <LI>Provident Fund (EPF) and ESIC registrations and returns</LI>
        <LI>Factories Act / Shops Act licences where the site or premises qualify</LI>
        <LI>Fire NOC and lift/elevator inspection certificates</LI>
        <LI>Height-work, hot-work or other site safety permits</LI>
        <LI>First-aid, safety officer and worker training records</LI>
      </UL>

      <H3 id="insurance-equipment">Insurance and equipment-related certificates</H3>
      <UL>
        <LI>Contractor's All-Risks (CAR) / Erection All-Risks (EAR) policies</LI>
        <LI>Workmen's Compensation / Employees' Compensation policies</LI>
        <LI>Public liability and third-party insurance</LI>
        <LI>Motor insurance, road tax, PUC and fitness certificates for site vehicles and machinery</LI>
        <LI>Equipment warranties and Annual Maintenance Contracts (AMCs)</LI>
        <LI>Load-test certificates for cranes, hoists, lifts and lifting tackle</LI>
        <LI>Electrical safety certificates for site power installations</LI>
        <LI>Calibration certificates for measuring and testing equipment</LI>
      </UL>
      <P>
        Fleet-related renewals often need special attention. For a deeper look at insurance, PUC, fitness and permit tracking for vehicles, see our{" "}
        <Link to="/fleet-document-expiry-reminder" className="font-semibold text-brand underline-offset-4 hover:underline">
          fleet document expiry reminder guide
        </Link>
        .
      </P>

      <H3 id="project-approvals">Site- and project-specific approvals</H3>
      <UL>
        <LI>Building plan approvals and commencement certificates</LI>
        <LI>Consent to Establish / Consent to Operate from the State Pollution Control Board</LI>
        <LI>Water and electricity connection approvals</LI>
        <LI>Tree-cutting, excavation or road-cutting permissions</LI>
        <LI>Client-side compliance submissions and bank guarantees</LI>
      </UL>

      <H2 id="why-manual-fails">Why manually tracking multiple renewal dates becomes difficult</H2>
      <P>
        Most construction offices start with a spreadsheet. It works — until it doesn't. The typical failure points look like this:
      </P>
      <UL>
        <LI>Documents live in email inboxes, WhatsApp threads, physical files and different laptops.</LI>
        <LI>Renewal dates get updated in one place but not another.</LI>
        <LI>The person who "owns" the spreadsheet changes projects or leaves the company.</LI>
        <LI>Reminders are set on personal calendars rather than a shared system.</LI>
        <LI>Some renewals need action 30+ days in advance, but the alert only comes when the date is imminent.</LI>
      </UL>
      <P>
        The result: at any given time, at least one renewal is closer to expiry than anyone realises. On a busy site, that gap is where fines and stop-work notices tend to appear.
      </P>

      <H2 id="checklist">A practical checklist for keeping renewal dates organised</H2>
      <P>
        You don't need enterprise software to fix this. A simple, disciplined process goes a long way:
      </P>
      <UL>
        <LI><strong>Make one master list.</strong> Every licence, permit, certificate, insurance policy and contract in one place — not scattered across drives.</LI>
        <LI><strong>Record the essentials for each item.</strong> Document name, category, issuing authority, expiry date, responsible person and where the current copy is stored.</LI>
        <LI><strong>Assign ownership.</strong> Every renewal should have a named owner, not a shared assumption.</LI>
        <LI><strong>Group by lead time.</strong> Some renewals need paperwork 30–60 days in advance. Others are quicker. Note the required lead time next to each item.</LI>
        <LI><strong>Set reminders <em>before</em> expiry.</strong> A reminder on the expiry date is often too late. Use tiered reminders — for example, 60, 30 and 7 days before.</LI>
        <LI><strong>Keep the document with the record.</strong> When a reminder fires, the current PDF or scan should be one click away.</LI>
        <LI><strong>Review monthly.</strong> A short monthly compliance review catches drift before it becomes a problem.</LI>
      </UL>
      <P>
        If you're new to organising renewal dates, our simple guide on{" "}
        <Link to="/blog/how-to-keep-track-of-document-expiry-dates" className="font-semibold text-brand underline-offset-4 hover:underline">
          how to keep track of document expiry dates
        </Link>{" "}
        walks through the manual method step by step.
      </P>

      <H2 id="reminder-system">How a renewal reminder system can help prevent missed deadlines</H2>
      <P>
        Once the list of renewals goes past a certain size, human memory and manual review are no longer reliable. A dedicated renewal reminder system helps in a few specific ways:
      </P>
      <UL>
        <LI><strong>Centralisation.</strong> All renewal dates, categories and owners live in one dashboard — not across notebooks and inboxes.</LI>
        <LI><strong>Configurable reminders.</strong> Email and push notifications go out ahead of each expiry with lead times you choose per item.</LI>
        <LI><strong>Clear visibility.</strong> Upcoming, active and expired items are visible at a glance instead of buried in a spreadsheet.</LI>
        <LI><strong>Custom categories.</strong> You can group renewals by site, project, licence type or department — whatever fits how your business is organised.</LI>
      </UL>

      <H2 id="renewtracker">Where RenewTracker fits in</H2>
      <P>
        <Link to="/" className="font-semibold text-brand underline-offset-4 hover:underline">
          RenewTracker
        </Link>{" "}
        is a renewal and expiry tracking app built exactly around this problem. It lets construction businesses add every important document, licence, permit, certificate, insurance policy, contract and equipment renewal in one place, and receive timely email and push reminders before expiry.
      </P>
      <P>
        Construction companies typically use it to:
      </P>
      <UL>
        <LI>Track contractor licences, BOCW and other labour registrations, PF/ESIC and trade licences</LI>
        <LI>Manage insurance renewals — CAR/EAR, workmen's compensation, third-party liability</LI>
        <LI>Keep vehicle and equipment renewals current — insurance, road tax, PUC, fitness certificates, AMCs, load-test certificates</LI>
        <LI>Store project-specific approvals and NOCs alongside their expiry dates</LI>
      </UL>
      <P>
        A few things worth being clear about: RenewTracker is a tracking and reminder tool. It helps you and your team remember, plan and act in time. It does <strong>not</strong> automatically renew documents, submit forms on your behalf or provide legal or compliance advice. You still complete each renewal with the relevant authority, insurer or provider — RenewTracker's role is to make sure you never miss the deadline.
      </P>

      <div className="mt-8 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        <p className="font-display text-lg font-semibold text-ink">
          Track your construction renewals in one place
        </p>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">
          Get email and push reminders before every important compliance date. Available now on Google Play.
        </p>
        <a
          href="https://play.google.com/store/apps/details?id=com.renewtracker.app"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="article-play-cta"
          aria-label="Get RenewTracker on Google Play"
          className="mt-5 inline-flex items-center gap-3 rounded-2xl bg-ink px-5 py-3.5 text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
        >
          <span className="grid h-6 w-6 place-items-center">
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path fill="#10B981" d="M6 3.5v17l14-8.5z" />
            </svg>
          </span>
          <span className="text-left leading-tight">
            <span className="block text-[10px] uppercase tracking-widest text-white/60">Get it on</span>
            <span className="block font-display text-base font-semibold">Google Play</span>
          </span>
          <span className="ml-2 rounded-full bg-greenaccent/20 px-2 py-0.5 text-[10px] font-semibold text-greenaccent">Available Now</span>
        </a>
      </div>

      <H2 id="final-thoughts">Final thoughts</H2>
      <P>
        Compliance is one of those parts of a construction business that stays quiet — until it doesn't. A single missed licence renewal or expired safety certificate can hold up a site far more than it should. The good news: the fix is mostly organisational. A single list, clear owners, sensible lead times and reliable reminders solve most of the problem. Whether you use a spreadsheet, a dedicated app like RenewTracker, or a combination of both, the point is the same — make sure no renewal date is remembered only in someone's head.
      </P>

      <div className="mt-14 rounded-2xl border border-slate-200 bg-background p-6 text-sm text-slate-600">
        Questions about tracking construction renewals?{" "}
        <Link to="/contact" className="font-semibold text-brand hover:underline">
          Get in touch
        </Link>
        {" "}— we're happy to help.
      </div>
    </article>
  );
}
